import {
  ATTRIBUTION_HISTORY_STATE_KEY,
  CAPTURE_KEY_PATTERN,
  extractClickIdsFromLocation,
  generateCaptureKey,
  persistAttributionTouch,
  stripAttributionFromCurrentHistoryEntry,
} from "@/lib/analytics/attributionCookies";
import { getMarketingSessionId } from "@/lib/analytics/marketingSession";
import { extractUtmFromLocation, toWireUtm, type UtmAttribution } from "@/lib/analytics/utm";
import { getAnalyticsLocation } from "@/lib/analytics/urlSanitization";
import { isNewVisit } from "@/lib/analytics/visitActivity";

type CapturedAttribution = {
  utm?: UtmAttribution;
};

type NavigationCaptureState = {
  fingerprint: string;
  captureKey: string;
  capturedAt: string;
  marketingSessionId: string;
};

let fallbackNavigationCapture: NavigationCaptureState | undefined;

const isNavigationCaptureState = (value: unknown): value is NavigationCaptureState => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Partial<NavigationCaptureState>;
  return (
    typeof candidate.fingerprint === "string" &&
    typeof candidate.captureKey === "string" &&
    CAPTURE_KEY_PATTERN.test(candidate.captureKey) &&
    typeof candidate.capturedAt === "string" &&
    Number.isFinite(Date.parse(candidate.capturedAt)) &&
    new Date(candidate.capturedAt).toISOString() === candidate.capturedAt &&
    typeof candidate.marketingSessionId === "string" &&
    CAPTURE_KEY_PATTERN.test(candidate.marketingSessionId)
  );
};

const createNavigationCapture = (fingerprint: string, marketingSessionId: string): NavigationCaptureState => ({
  fingerprint,
  captureKey: generateCaptureKey(),
  capturedAt: new Date().toISOString(),
  marketingSessionId,
});

const getNavigationCapture = (fingerprint: string, marketingSessionId: string): NavigationCaptureState | undefined => {
  if (typeof window !== "undefined") {
    const historyState = window.history.state as unknown;
    if (historyState === null || (typeof historyState === "object" && !Array.isArray(historyState))) {
      const stateRecord = (historyState ?? {}) as Record<string, unknown>;
      const previous = stateRecord[ATTRIBUTION_HISTORY_STATE_KEY];
      if (isNavigationCaptureState(previous)) {
        // Back/Forward can restore an attributed SPA entry created before an account boundary.
        // Never reinterpret that old address-bar attribution as a new owner's acquisition touch.
        if (previous.marketingSessionId !== marketingSessionId) {
          stripAttributionFromCurrentHistoryEntry();
          return undefined;
        }
        if (previous.fingerprint === fingerprint) return previous;
      }

      const capture = createNavigationCapture(fingerprint, marketingSessionId);
      window.history.replaceState(
        { ...stateRecord, [ATTRIBUTION_HISTORY_STATE_KEY]: capture },
        "",
        window.location.href,
      );
      return capture;
    }
  }

  if (
    fallbackNavigationCapture?.fingerprint === fingerprint &&
    fallbackNavigationCapture.marketingSessionId === marketingSessionId
  ) {
    return fallbackNavigationCapture;
  }
  fallbackNavigationCapture = createNavigationCapture(fingerprint, marketingSessionId);
  return fallbackNavigationCapture;
};

const getReferrerOrigin = (): string | undefined => {
  if (typeof document === "undefined" || !document.referrer) return undefined;
  try {
    const parsed = new URL(document.referrer);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return undefined;
    // Truncating an origin can manufacture an invalid hostname and make the server reject the
    // entire otherwise-valid attribution snapshot. Oversized referrers are simply not useful.
    return parsed.origin.length <= 255 ? parsed.origin : undefined;
  } catch {
    return undefined;
  }
};

/** The referrer of a new visit arriving from another site: organic search, an AI assistant, a link. */
const getNewVisitReferrerOrigin = (): string | undefined => {
  const referrerOrigin = getReferrerOrigin();
  if (!referrerOrigin || typeof window === "undefined") return undefined;
  if (new URL(referrerOrigin).hostname === window.location.hostname) return undefined;
  return isNewVisit() ? referrerOrigin : undefined;
};

/**
 * Persists any tracking params on the given query string into the attribution cookies:
 * utm plus paid-click ids (gclid/msclkid/gbraid/wbraid/fbclid), combined into ONE write per cookie —
 * write-once for `ee_attribution_first`, and a whole-snapshot replacement for
 * `ee_attribution_last`. Values from different navigations are never merged into a synthetic
 * touch.
 *
 * The combined write matters for first touch: a gclid-only ad landing (Google auto-tagging
 * appends no `utm_*`) is still the lead's first touch and must claim the write-once cookie,
 * exactly like a utm-carrying landing. Returns the captured utm when the navigation carried
 * any, `{}` for a touch without utm, and undefined when it captured nothing.
 *
 * An untagged new visit from another site is a touch too, holding only its referrer, so the last
 * touch is the last non-direct visit: without it, organic search and AI-assistant visits would
 * keep crediting whatever ad was clicked in the previous 90 days. A direct visit (no referrer)
 * leaves the last touch alone.
 *
 * Idempotent per navigation so remounts or repeated relay effects on the same URL stay harmless.
 */
export const captureAttributionFromSearch = (locationSearch: string): CapturedAttribution | undefined => {
  let effectiveSearch = locationSearch;
  let utm = extractUtmFromLocation(effectiveSearch);
  let clickIds = extractClickIdsFromLocation(effectiveSearch);
  if (!utm && !clickIds && !getNewVisitReferrerOrigin()) {
    return undefined;
  }

  // Reading the shared journey may synchronously adopt another tab's replacement. Its boundary
  // listener strips old-owner attribution from this history entry, so re-read the live query
  // before persisting anything when the caller supplied that same pre-boundary query.
  const browserSearchBeforeIdentityRead = typeof window === "undefined" ? undefined : window.location.search;
  const marketingSessionId = getMarketingSessionId();
  if (
    browserSearchBeforeIdentityRead !== undefined &&
    effectiveSearch === browserSearchBeforeIdentityRead &&
    window.location.search !== browserSearchBeforeIdentityRead
  ) {
    effectiveSearch = window.location.search;
    // Another tab's owner boundary just stripped this entry's attribution; never fall back to a
    // referrer touch for the replacement owner.
    utm = extractUtmFromLocation(effectiveSearch);
    clickIds = extractClickIdsFromLocation(effectiveSearch);
    if (!utm && !clickIds) return undefined;
  }

  const currentUrl =
    typeof window === "undefined" ? `https://analytics.invalid/${effectiveSearch}` : window.location.href;
  const landingPath = (getAnalyticsLocation(currentUrl)?.path ?? "/").slice(0, 2000);
  const fingerprint = `${landingPath}?${new URLSearchParams(effectiveSearch).toString()}`;
  const referrerOrigin = getReferrerOrigin();
  const capture = getNavigationCapture(fingerprint, marketingSessionId);
  if (!capture) return undefined;
  const fields = {
    landingPath,
    ...(referrerOrigin ? { referrerOrigin } : {}),
    ...(utm ? { utm: toWireUtm(utm) } : {}),
    ...clickIds,
  };
  persistAttributionTouch(fields, capture);

  return { ...(utm ? { utm } : {}) };
};
